import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, MessageCircle, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

/**
 * HelpModal - Replaces discount popup with help-focused exit intent
 * Shows WhatsApp CTA and Continue options only - no discounts
 */
const HelpModal = ({ 
  pagePath = "/quote",
  pickupAirport = '',
  destination = '',
  date = '',
  time = '',
  passengers = 1,
  luggage = 1
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const SESSION_KEY = `help_modal_shown_${pagePath.replace(/\//g, '_')}`;
  
  // Check if modal was already shown in this session
  const wasShownInSession = useCallback(() => {
    return sessionStorage.getItem(SESSION_KEY) === 'true';
  }, [SESSION_KEY]);
  
  // Mark modal as shown in session
  const markAsShown = useCallback(() => {
    sessionStorage.setItem(SESSION_KEY, 'true');
  }, [SESSION_KEY]);
  
  // Show modal
  const showModal = useCallback(() => {
    if (wasShownInSession()) return;
    setIsVisible(true);
    markAsShown();
  }, [wasShownInSession, markAsShown]);
  
  // Close modal
  const closeModal = () => {
    setIsVisible(false);
  };
  
  // Generate WhatsApp message with booking context
  const getWhatsAppUrl = () => {
    const phoneNumber = '905352385797';
    const message = encodeURIComponent(
      `Hi! I need help with my transfer booking. ` +
      `Route: ${pickupAirport || 'Airport'} -> ${destination || 'Destination'} | ` +
      `Date: ${date || 'Not set'} ${time || ''} | ` +
      `Passengers: ${passengers} | Luggage: ${luggage}. ` +
      `I'm on the Vehicle step.`
    );
    return `https://wa.me/${phoneNumber}?text=${message}`;
  };
  
  // Exit intent - detect mouse leaving viewport (desktop)
  useEffect(() => {
    if (wasShownInSession()) return;
    
    const handleMouseLeave = (e) => {
      // Only trigger when mouse leaves from top of page (exit intent)
      if (e.clientY <= 0) {
        showModal();
      }
    };
    
    // Add listener after a delay to avoid immediate trigger
    const addListenerTimer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000); // Wait 5 seconds before enabling exit intent
    
    return () => {
      clearTimeout(addListenerTimer);
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [showModal, wasShownInSession]);
  
  // Back button detection (mobile & desktop)
  useEffect(() => {
    if (wasShownInSession()) return;
    
    // Push a state to detect back button
    window.history.pushState({ helpModal: true }, '');
    
    const handlePopState = (e) => {
      if (!wasShownInSession()) {
        showModal();
        // Push state again to prevent actual navigation
        window.history.pushState({ helpModal: true }, '');
      }
    };
    
    window.addEventListener('popstate', handlePopState);
    
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [showModal, wasShownInSession]);
  
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isVisible) {
        closeModal();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isVisible]);
  
  if (!isVisible) return null;
  
  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={closeModal}
      data-testid="help-modal-overlay"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in duration-300" />
      
      {/* Modal */}
      <div 
        className="relative bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 rounded-2xl p-6 md:p-8 max-w-md w-full border border-white/10 shadow-2xl animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
        data-testid="help-modal"
      >
        {/* Close button */}
        <button
          onClick={closeModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
          data-testid="help-modal-close"
        >
          <X className="w-5 h-5" />
        </button>
        
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
            <MessageCircle className="w-8 h-8 text-white" />
          </div>
        </div>
        
        {/* Title */}
        <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-2">
          Need Help?
        </h2>
        
        {/* Subtitle */}
        <p className="text-gray-400 text-center mb-6">
          Have questions about your transfer? Our team is here to help you 24/7.
        </p>
        
        {/* WhatsApp CTA */}
        <a
          href={getWhatsAppUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="w-full h-12 bg-[#25D366] hover:bg-[#20BD5A] text-white font-semibold text-lg rounded-xl flex items-center justify-center gap-2 mb-3 transition-colors"
          data-testid="help-modal-whatsapp"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          Chat on WhatsApp
        </a>
        
        {/* Continue Booking Button */}
        <Button
          onClick={closeModal}
          variant="outline"
          className="w-full h-12 border-white/20 text-white hover:bg-white/10 font-semibold text-lg rounded-xl"
          data-testid="help-modal-continue"
        >
          Continue Booking
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        
        {/* Support info */}
        <p className="text-center text-gray-500 text-xs mt-4">
          Average response time: under 5 minutes
        </p>
      </div>
    </div>
  );
};

export default HelpModal;
